import { type CalendarDate, type Time } from '@internationalized/date';
import React from 'react';
import { combineToJsDate, jsDateToCalendarDate, jsDateToTime } from './dateUtils';

/**
 * usePopoverDateValue — holds the popover pickers' working value as the two pieces react-aria needs (a
 * `CalendarDate` for `Calendar` and a `Time` for `TimeField`) and centralizes the `@internationalized/date` ⇄
 * plain `Date` glue, so the three pickers (`DatePicker`, `DatePickerActions`, `DatePickerInline`) don't
 * reimplement the date+time state (they still use `dateUtils` for one-offs like min/max/display). It is the format
 * bridge + memory of the two halves — not a "draft" by itself: `DatePicker` commits it live on every change,
 * while `DatePickerActions` treats it as a draft until Save.
 *
 * `changeDate`/`changeTime` update one half and return the recombined JS `Date` (handy for emitting an
 * `onChange`); `setFromJsDate` reseeds both halves from a JS `Date`; `jsDate` is the current recombined value.
 */
type UsePopoverDateValueReturn = {
  changeDate: (nextDate: CalendarDate) => Date | null;
  changeTime: (nextTime: Time | null) => Date | null;
  dateValue: CalendarDate | null;
  jsDate: Date | null;
  setFromJsDate: (date: Date | null) => void;
  timeValue: Time | null;
};

export function usePopoverDateValue(initialDate: Date | null): UsePopoverDateValueReturn {
  const [dateValue, setDateValue] = React.useState<CalendarDate | null>(() => jsDateToCalendarDate(initialDate));
  const [timeValue, setTimeValue] = React.useState<Time | null>(() => jsDateToTime(initialDate));

  function changeDate(nextDate: CalendarDate): Date | null {
    setDateValue(nextDate);
    return combineToJsDate(nextDate, timeValue);
  }

  function changeTime(nextTime: Time | null): Date | null {
    setTimeValue(nextTime);
    return combineToJsDate(dateValue, nextTime);
  }

  const setFromJsDate = React.useCallback((date: Date | null) => {
    setDateValue(jsDateToCalendarDate(date));
    setTimeValue(jsDateToTime(date));
  }, []);

  return {
    changeDate,
    changeTime,
    dateValue,
    jsDate: combineToJsDate(dateValue, timeValue),
    setFromJsDate,
    timeValue,
  };
}
